import { useMemberships } from "@bodhistreams/api/src";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	enrichPlansWithAvailability,
	PAID_PLANS_ENABLED,
	PLAN_COLORS,
	PLAN_TAGLINES,
	planRank,
} from "../../../config/pricing";
import "./PricingPreview.css";

const CheckIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
		<path
			d="M5 13l4 4L19 7"
			stroke="currentColor"
			strokeWidth="3"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const CrossIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
		<path
			d="M6 6l12 12M18 6L6 18"
			stroke="currentColor"
			strokeWidth="2.5"
			strokeLinecap="round"
		/>
	</svg>
);

const ArrowIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
		<path
			d="M5 12h14M13 5l7 7-7 7"
			stroke="currentColor"
			strokeWidth="2.2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const buildPlan = (membership) => {
	const key = membership.name.toLowerCase();
	const isFree = key === "free" || membership.price === 0;
	const devices = membership.max_simultaneous_devices;
	return {
		id: membership.id,
		key,
		title: membership.name,
		tagline: PLAN_TAGLINES[key] || "",
		monthly: Number(membership.price),
		yearly: Number(membership.yearly_price || membership.price * 12),
		color: PLAN_COLORS[key] || "#6B9E8D",
		highlighted: PAID_PLANS_ENABLED && key === "premium",
		features: [
			{
				text: `${devices} device${devices > 1 ? "s" : ""} at once`,
				enabled: true,
			},
			{
				text: membership.streaming_hours
					? `${membership.streaming_hours} hrs / month`
					: "Unlimited streaming",
				enabled: true,
			},
			{
				text: isFree ? "Basic collections" : "Full library access",
				enabled: true,
			},
			{
				text: "Playlist playback",
				enabled: !isFree,
			},
			{
				text: "Commercial use",
				enabled: !isFree,
			},
		],
	};
};

const PricingPreview = () => {
	const navigate = useNavigate();
	const [yearlyBilling, setYearlyBilling] = useState(false);
	const { data: membershipsData, isLoading } = useMemberships();
	const memberships = Array.isArray(membershipsData) ? membershipsData : [];

	const plans = enrichPlansWithAvailability(
		memberships
			.map(buildPlan)
			.sort((a, b) => planRank(a.key) - planRank(b.key)),
	);

	const handleSelect = (plan) => {
		if (plan.disabled) return;
		navigate(`/pricing?plan=${plan.key}`);
	};

	const handleCompare = () => {
		navigate("/pricing");
	};

	if (isLoading && plans.length === 0) {
		return (
			<section className="pricing-preview">
				<div className="pricing-preview-loading">
					<div className="pricing-preview-loading-spinner" />
				</div>
			</section>
		);
	}

	return (
		<section className="pricing-preview">
			<p className="pricing-preview-intro">
				Start free, upgrade when you're ready. Every plan unlocks original 432
				Hz wellness music — choose how much of the library and how many devices
				fit your practice.
			</p>

			{PAID_PLANS_ENABLED && (
				<div className="pricing-preview-toggle">
					<button
						type="button"
						className="pricing-preview-switch"
						data-checked={yearlyBilling ? "true" : "false"}
						onClick={() => setYearlyBilling((y) => !y)}
						aria-label="Toggle yearly billing"
					>
						<span className="pricing-preview-switch-label is-monthly">
							Monthly
						</span>
						<span className="pricing-preview-switch-label is-yearly">
							Yearly
						</span>
						<span className="pricing-preview-switch-thumb" />
					</button>
					{yearlyBilling && (
						<span className="pricing-preview-save-hint">Save up to 25%</span>
					)}
				</div>
			)}

			<div className="pricing-preview-grid">
				{plans.map((plan) => {
					const price = yearlyBilling ? plan.yearly : plan.monthly;
					const savings = plan.monthly * 12 - plan.yearly;
					const showSavings = yearlyBilling && plan.yearly > 0 && savings > 0;

					return (
						<button
							type="button"
							key={plan.id}
							onClick={() => handleSelect(plan)}
							disabled={plan.disabled}
							className={[
								"pricing-preview-card",
								plan.highlighted && "is-highlighted",
								plan.disabled && "is-disabled",
							]
								.filter(Boolean)
								.join(" ")}
							style={{ "--plan-color": plan.color }}
						>
							{plan.disabled && (
								<span className="pricing-preview-badge is-soon">
									Coming Soon
								</span>
							)}
							{!plan.disabled && plan.highlighted && (
								<span className="pricing-preview-badge">Most Popular</span>
							)}

							<span className="pricing-preview-title">{plan.title}</span>
							<p className="pricing-preview-tagline">{plan.tagline}</p>

							<div className="pricing-preview-price">
								${price.toFixed(2)}
								<span className="pricing-preview-price-period">
									/{yearlyBilling ? "yr" : "mo"}
								</span>
							</div>
							<div className="pricing-preview-save">
								{showSavings ? `Save $${savings.toFixed(0)}/yr` : ""}
							</div>

							<div className="pricing-preview-divider" />

							<ul className="pricing-preview-features">
								{plan.features.map((feature) => (
									<li
										key={feature.text}
										className={`pricing-preview-feature${
											feature.enabled ? "" : " is-muted"
										}`}
									>
										<span className="pricing-preview-feature-icon">
											{feature.enabled ? <CheckIcon /> : <CrossIcon />}
										</span>
										<span>{feature.text}</span>
									</li>
								))}
							</ul>

							<span className="pricing-preview-cta">
								{plan.disabled ? "Coming Soon" : `Choose ${plan.title}`}
								{!plan.disabled && (
									<span className="pricing-preview-cta-arrow">→</span>
								)}
							</span>
						</button>
					);
				})}
			</div>

			<div className="pricing-preview-footer">
				{PAID_PLANS_ENABLED && (
					<button
						type="button"
						className="pricing-preview-compare"
						onClick={handleCompare}
					>
						Compare all features
						<span className="pricing-preview-compare-arrow">
							<ArrowIcon />
						</span>
					</button>
				)}
				<span className="pricing-preview-footnote">
					{PAID_PLANS_ENABLED
						? "Cancel anytime · No commitment"
						: "Free to use · No credit card required"}
				</span>
			</div>
		</section>
	);
};

export default PricingPreview;
